import { useContext, useEffect, useMemo, useState } from "react";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "../firebase/firebase";
import { ContractContext } from "../providers/contract";
import {
  CashOutAmount,
  ESenderRole,
  ETrasactionsType,
  IContractDetailsResponseData,
} from "../setup/api/services/contract/models";
import { resolveCashoutPartyRole } from "../setup/util/userIdentifier";

export interface ISettlementActionsConfig {
  settledAfter: string;
  maxItemPrice: number;
  gracePeriodInHours: number;
  excludedOrgs: string[];
}

// Fetches the remote-config thresholds that govern auto-cashout eligibility.
// Shared across features so the config is only fetched once per consumer tree.
export const useSettlementActionsConfig = (): {
  config: ISettlementActionsConfig | null;
  loading: boolean;
} => {
  const [config, setConfig] = useState<ISettlementActionsConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        await fetchAndActivate(remoteConfig);
        const excludedOrgs = getValue(remoteConfig, "excludedOrgs")
          .asString()
          .split(",")
          .map((org) => org.trim())
          .filter(Boolean);

        setConfig({
          settledAfter: getValue(remoteConfig, "settledAfter").asString(),
          maxItemPrice: getValue(remoteConfig, "maxItemPrice").asNumber(),
          gracePeriodInHours: getValue(
            remoteConfig,
            "gracePeriodInHours"
          ).asNumber(),
          excludedOrgs,
        });
      } catch (error) {
        console.error("Error fetching settlement actions config:", error);
        setConfig(null);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  return { config, loading };
};

// True when the given user has a prior failed cashout transaction on this
// contract, meaning they shouldn't be auto-cashed-out without manual review.
const hasFailedCashoutTransaction = (
  contract: IContractDetailsResponseData | undefined,
  userIdentification: string | undefined
): boolean => {
  if (!userIdentification) return false;
  return !!contract?.transactions?.some(
    (transaction) =>
      transaction.beneficiaryId === String(userIdentification) &&
      transaction.status === "FAILED" &&
      transaction.type === ETrasactionsType.CASH_OUT
  );
};

// Contract-wide conditions for auto-cashout that don't depend on which
// specific party/cashOutAmount is being considered.
const isBaselineEligibleForAutoCashout = (
  contract: IContractDetailsResponseData | undefined,
  config: ISettlementActionsConfig | null
): boolean => {
  if (!config || !contract?.settledAt) return false;

  const settledAt = new Date(contract.settledAt);
  const isSettledAfterConfigDate = settledAt > new Date(config.settledAfter);
  const isItemPriceBelowMax = contract.itemPrice <= config.maxItemPrice;
  const isOrgExcluded = !!(
    contract.organizationCode &&
    config.excludedOrgs.includes(contract.organizationCode)
  );
  const hasManualBankTransfer = !!contract.transactions?.some(
    (transaction) =>
      transaction.type === ETrasactionsType.MANUAL_BANK_TRANSFER &&
      transaction.status === "SUCCESS"
  );

  return (
    isSettledAfterConfigDate &&
    isItemPriceBelowMax &&
    !isOrgExcluded &&
    !hasManualBankTransfer
  );
};

const isPayableProviderOrBrokerCashout = (
  contract: IContractDetailsResponseData | undefined,
  cashOutAmount: CashOutAmount
): boolean => {
  const role = resolveCashoutPartyRole(
    contract,
    cashOutAmount.userIdentification
  );
  return (
    (role === ESenderRole.PROVIDER || role === ESenderRole.BROKER) &&
    (cashOutAmount.cashOutAmount || 0) > 0
  );
};

// True when a contract's price, organization, and settlement date make it
// eligible for the automated cashout flow (grace period aside). Ineligible
// as soon as any payable provider/broker has a prior failed cashout, since
// that party needs manual review.
export const isEligibleForAutoCashout = (
  contract: IContractDetailsResponseData | undefined,
  config: ISettlementActionsConfig | null
): boolean => {
  if (!isBaselineEligibleForAutoCashout(contract, config)) return false;

  const payableCashouts = contract?.cashOutAmountList?.filter((cashOutAmount) =>
    isPayableProviderOrBrokerCashout(contract, cashOutAmount)
  );
  if (!payableCashouts?.length) return false;

  return !payableCashouts.some((cashOutAmount) =>
    hasFailedCashoutTransaction(contract, cashOutAmount.userIdentification)
  );
};

// Same as isEligibleForAutoCashout, but scoped to a single cashOutAmount:
// eligible only if that specific party is payable and has no prior failed
// cashout, regardless of whether other parties on the contract have failed.
export const isCashoutAmountEligibleForAutoCashout = (
  contract: IContractDetailsResponseData | undefined,
  cashOutAmount: CashOutAmount,
  config: ISettlementActionsConfig | null
): boolean => {
  if (!isBaselineEligibleForAutoCashout(contract, config)) return false;
  if (!isPayableProviderOrBrokerCashout(contract, cashOutAmount)) return false;
  return !hasFailedCashoutTransaction(
    contract,
    cashOutAmount.userIdentification
  );
};

export const getAutoCashoutGracePeriodDeadline = (
  contract: IContractDetailsResponseData | undefined,
  config: ISettlementActionsConfig | null
): Date | null => {
  if (!config || !contract?.settledAt) return null;
  const settledAt = new Date(contract.settledAt);
  return new Date(
    settledAt.getTime() + config.gracePeriodInHours * 60 * 60 * 1000
  );
};

const getShouldHideSettlementActions = (
  contract: IContractDetailsResponseData | undefined,
  config: ISettlementActionsConfig | null
): boolean => {
  if (!isEligibleForAutoCashout(contract, config)) return false;
  const deadline = getAutoCashoutGracePeriodDeadline(contract, config);
  return !!deadline && new Date() > deadline;
};

// Hides the cashout / re-settlement actions once a settled contract falls below
// the configured price threshold and has passed the settlement grace period.
export const useShouldHideSettlementActions = (): boolean => {
  const { contract } = useContext(ContractContext);
  const { config } = useSettlementActionsConfig();

  return useMemo(() => {
    return getShouldHideSettlementActions(contract, config);
  }, [contract, config]);
};

const getShouldHideSettlementActionsForCashoutAmount = (
  contract: IContractDetailsResponseData | undefined,
  cashOutAmount: CashOutAmount,
  config: ISettlementActionsConfig | null
): boolean => {
  if (!isCashoutAmountEligibleForAutoCashout(contract, cashOutAmount, config))
    return false;
  const deadline = getAutoCashoutGracePeriodDeadline(contract, config);
  return !!deadline && new Date() > deadline;
};

// Per-party variant of useShouldHideSettlementActions: hides the action for
// this specific cashOutAmount only if it individually qualifies for
// auto-cashout, so a party with a prior failed cashout still shows its
// manual action even when other parties on the same contract are hidden.
export const useShouldHideSettlementActionsForCashoutAmount = (
  cashOutAmount: CashOutAmount
): boolean => {
  const { contract } = useContext(ContractContext);
  const { config } = useSettlementActionsConfig();

  return useMemo(() => {
    return getShouldHideSettlementActionsForCashoutAmount(
      contract,
      cashOutAmount,
      config
    );
  }, [contract, cashOutAmount, config]);
};
